import React, { FC, useEffect, useState } from 'react';
import TicketTable from './TicketTable';
import Ticket, { TicketClosedReason, TicketSearchCriteria } from 'src/interfaces/api/Ticket';
import Employee from 'src/interfaces/api/Employee';
import useTicket from 'src/hooks/api/useTicket';
import useEmployeeAccess from 'src/hooks/api/useEmployeeAccess';
import Site from 'src/interfaces/api/Site';
import { unique } from 'src/utils/ArrayUtils';
import useSite from 'src/hooks/api/useSite';

interface Props {
  searchCriteria: TicketSearchCriteria;
}

const TicketTableContainer: FC<Props> = ({ searchCriteria, ...props }) => {
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState(0);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  const { closedReasons } = useClosedReasonsFetcher();
  const { loading: employeesLoading, error: employeesError, employees } = useEmployeesFetcher();
  const { loading, error, total, tickets } = useTicketsFetcher(searchCriteria, limit, page, orderBy, order);
  const { loading: sitesLoading, error: sitesError, sites } = useSitesFetcher(tickets);

  useEffect(() => {
    setPage(0);
  }, [searchCriteria]);

  const handleChangeLimit = (event: React.ChangeEvent<HTMLInputElement>) => {
    const limit = parseInt(event.target.value, 10);
    setLimit(limit);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRequestSort = (newOrderBy: string) => {
    const isAsc = orderBy === newOrderBy && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(newOrderBy);
    setPage(0);
  };

  return (
    <TicketTable
      tickets={tickets}
      loading={loading}
      total={total}
      page={page}
      orderBy={orderBy}
      limit={limit}
      order={order}
      error={error}
      employees={employees}
      sites={sites}
      sitesLoading={sitesLoading}
      sitesError={sitesError}
      employeesLoading={employeesLoading}
      employeesError={employeesError}
      closedReasons={closedReasons}
      onRequestSort={handleRequestSort}
      onChangeLimit={handleChangeLimit}
      onChangePage={handleChangePage}
      {...props}
    />
  );
};

const useSitesFetcher = (tickets: Ticket[]) => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const { getBatch } = useSite();

  useEffect(() => {
    const cachedIds = sites.map((site) => site.id);
    let sitesIds = unique(tickets.map((ticket) => ticket.siteId));
    sitesIds = sitesIds.filter((id) => id && !cachedIds.includes(id));

    if (sitesIds.length > 0 && !loading) {
      setError(undefined);
      setLoading(true);
      getBatch(sitesIds as string[])
        .then((collection) => {
          setSites([...sites, ...collection.items]);
          setLoading(false);
        })
        .catch((e) => {
          setError(e.message);
          setLoading(false);
        });
    }
  }, [tickets]);

  return { error, loading, sites };
};

const useClosedReasonsFetcher = () => {
  const [closedReasons, setClosedReasons] = useState<TicketClosedReason[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const { getClosedReasons } = useTicket();

  useEffect(() => {
    setLoading(true);
    getClosedReasons()
      .then((collection) => {
        setClosedReasons(collection.items);

        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  return { error, loading, closedReasons };
};

const useTicketsFetcher = (
  criteria: TicketSearchCriteria,
  limit: number,
  page: number,
  orderBy?: string,
  order?: 'asc' | 'desc'
) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [total, setTotal] = useState(0);
  const { search } = useTicket();

  useEffect(() => {
    setLoading(true);
    search(criteria, page + 1, limit, orderBy, order)
      .then((collection) => {
        setTickets(collection.items);
        setTotal(collection.total);

        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, [criteria, limit, page, order, orderBy]);

  return { error, loading, total, tickets };
};

const useEmployeesFetcher = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  const { getAll } = useEmployeeAccess();

  useEffect(() => {
    setLoading(true);
    getAll()
      .then((collection) => {
        setEmployees(collection.items);

        setLoading(false);
      })
      .catch((e) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  return { error, loading, employees };
};

export default TicketTableContainer;
